import { Children, Fragment, isValidElement, useState, type ReactNode } from "react";
import { useT } from "../i18n";
import { Leersatz, Zeilenblatt, Zweizeiler, type Punkt } from "./Handyformen";
import { Blattvonunten } from "./Blattvonunten";
import type { Gruppe, Handyplatz, Spalte } from "./Tabelle";
import "../stile/telefon/tabelle.css";

/* ══════════════════════════════════════════════════════════════════
   Die Tabelle am Telefon als Zeilen (19.09.2026).

   Luca: *„Die Tabellenstruktur musst du auch einmal umbauen."* — nach dem
   Satz vom selben Tag: *„…das muss einheitlich sein von der Struktur,
   sodass die Buttons sauber sind und diese Einträge in der Liste immer
   gleich hoch sind."*

   ── Was vorher da war ─────────────────────────────────────────────

   Am Telefon wurde jede Tabellenzeile eine **Karte**: der Träger als
   Überschrift, darunter jede Spalte mit ihrer Beschriftung, die Marken
   verstreut — in den Einstellungen gut 200 px je Benutzer, je Kreis, je
   Job, und jede Karte so hoch, wie ihre Werte lang waren. Tippte man sie
   an, klappte die Bearbeitung **unter** ihr auf und schob die Liste weg.

   ── Was jetzt gilt ────────────────────────────────────────────────

   **Jede Zeile, die sich öffnen lässt, ist ein `Zweizeiler`** — dieselbe
   Zeile wie auf Start, Heute und in der Suche:

   | Platz der Spalte (`handy`) | steht in der Zeile |
   |---|---|
   | `kennung` | vorn im Titel, in Mono |
   | `titel` | der Titel |
   | `unter` | die zweite Zeile, Werte mit „ · " getrennt |
   | `marke` | rechts, gestapelt |
   | `breit`, `satz`, `weg` | nicht in der Zeile — sie stehen im Blatt |

   **Die Bearbeitung steht im Blatt, nicht unter der Zeile.** Was die Maske
   als `unterzeile` liefert, geht am Telefon in ein `Blattvonunten`; es
   schließt über denselben Weg, den die Maske schon hat (`klick` schaltet
   ihren Zustand zurück). Keine Maske muss dafür etwas ändern.

   **Wo das nicht gilt:** Eine Tabelle, die sich nicht antippen lässt, hat
   ihre Bedienung in der Zeile (Schalter, Wähler) — sie bleibt eine Karte.
   Dasselbe für Auswahl-Kästchen und Eingabezeilen (`auswahl`, `anhang`).
   ══════════════════════════════════════════════════════════════════ */

/** Der Text in einem Stück Markup — für den Titel des Blatts. */
function textVon(knoten: ReactNode): string {
  if (knoten === null || knoten === undefined || typeof knoten === "boolean") return "";
  if (typeof knoten === "string" || typeof knoten === "number") return String(knoten);
  if (Array.isArray(knoten)) return knoten.map(textVon).join("");
  if (isValidElement<{ children?: ReactNode; "aria-hidden"?: unknown }>(knoten)) {
    /* Was dem Vorleser verborgen ist (die Nummer vor einem Weg in Info),
       gehört auch nicht in den Titel des Blatts. */
    if (knoten.props["aria-hidden"] === true || knoten.props["aria-hidden"] === "true") return "";
    return textVon(knoten.props.children);
  }
  return "";
}

/**
 * Die Titelzelle ist oft eine `Sache` (`Tabelle.tsx`): Name und darunter eine
 * zweite Zeile — die E-Mail eines Benutzers, der Satz zu einem Nummernkreis.
 * In der Karte stand das untereinander; in der Zeile machte es jede zweite
 * Zeile höher als ihre Nachbarin (gemessen 57 gegen 87 px). **Der Name bleibt
 * Titel, die Nummer rückt in Mono dahinter, und der Zusatz geht ans Ende der
 * zweiten Zeile** — dort wird er abgeschnitten statt die Zeile aufzureißen.
 */
type Sachteile = { name: ReactNode; nr?: ReactNode; dazu?: ReactNode };
function alsSache(knoten: ReactNode): Sachteile | null {
  if (!isValidElement<Record<string, unknown>>(knoten)) return null;
  /* Eine Zelle, die `Sache` und etwas dahinter in ein Fragment packt (die
     Frage einer Checkliste samt ihren Möglichkeiten): Es zählt die `Sache`
     vorn; was dahinter steht, gehört ins Blatt und nicht in die Zeile. */
  if (knoten.type === Fragment) {
    return alsSache(Children.toArray(knoten.props["children"] as ReactNode)[0]);
  }
  const p = knoten.props;
  if (!("name" in p) || !("nr" in p || "dazu" in p)) return null;
  return { name: p["name"] as ReactNode, nr: p["nr"] as ReactNode, dazu: p["dazu"] as ReactNode };
}

/** Ist aus einer Zelle nichts geworden? Dann bleibt sie aus der Zeile heraus. */
function leer(knoten: ReactNode): boolean {
  return Children.toArray(knoten).length === 0;
}

/**
 * Der Blattmodus (`telefonblatt` an der Tabelle): für Tabellen, deren Bedienung
 * **in** der Zeile steht — ein Wähler je Typ bei den Zählerständen, ein
 * Schalter je Rolle bei den Pflichtfeldern. In einer Zeile von 60 px ist dafür
 * kein Platz, und als Karte war jede Zeile so hoch wie ihre Schalter. Also:
 * Die Zeile zeigt, worum es geht (Titel, Marken, ein Satz der Maske), und ein
 * Tipp öffnet das Blatt mit **allen** Spalten als beschriftete Felder — die
 * Bedienung ist dieselbe, sie steht nur einen Tipp tiefer.
 */
export type Telefonblatt<Z> = {
  /** Die zweite Zeile — ein Satz, der den Stand zusammenfasst. Ohne ihn
      stehen dort die Spalten mit Platz `unter`, wie sonst auch. */
  unter?: (z: Z) => ReactNode;
  /** Ein eigener Titel für Zeile und Blatt — wenn die Trägerzelle mehr ist
      als ein Name (ein Name mit Erklärsatz, ein Eingabefeld zum Umbenennen). */
  titel?: (z: Z) => ReactNode;
  /** Ein Satz oben im Blatt, vor den Feldern. */
  satz?: (z: Z) => ReactNode;
  /** Welche Spalten im Blatt stehen. Ohne Angabe: alle außer dem Titel.
      Mit Angabe auch die Trägerspalte — die Rolle, die sich in ihrer Zelle
      umbenennen lässt, braucht ihr Eingabefeld im Blatt, denn in eine Zeile,
      die ein Knopf ist, gehört keins. */
  felder?: string[];
};

export function Tabellenzeilen<Z>({
  spalten, plaetze, bloecke, id, klick: klickDerMaske, unterzeile: unterzeileDerMaske, punkt, laedt,
  leerSatz, fuss, ladezeilen, schild, telefonblatt,
}: {
  spalten: Spalte<Z>[];
  plaetze: Record<string, Handyplatz>;
  bloecke: Gruppe<Z>[];
  id: (z: Z) => string;
  klick?: ((z: Z) => void) | undefined;
  telefonblatt?: Telefonblatt<Z> | undefined;
  unterzeile?: ((z: Z) => ReactNode | null) | undefined;
  punkt?: ((z: Z) => Punkt) | undefined;
  laedt?: boolean | undefined;
  leerSatz?: ReactNode;
  fuss?: ReactNode;
  ladezeilen: number;
  schild: string;
}) {
  const t = useT();
  const trenn = ` ${t("allgemein.trennpunkt")} `;
  /* Im Blattmodus hält die Tabelle selbst fest, welche Zeile offen ist; sonst
     die Maske (über `klick` und `unterzeile`). */
  const [offenId, setzeOffenId] = useState<string | null>(null);
  const klick = klickDerMaske
    ?? ((z: Z) => setzeOffenId((alt) => (alt === id(z) ? null : id(z))));
  const unterzeile = unterzeileDerMaske ?? (telefonblatt
    ? (z: Z): ReactNode | null => (id(z) === offenId ? (
      <div className="tbz-felder">
        {telefonblatt.satz ? <p className="tbz-satz">{telefonblatt.satz(z)}</p> : null}
        {spalten.filter((sp) => (telefonblatt.felder
          ? telefonblatt.felder.includes(sp.id)
          : plaetze[sp.id] !== "titel")).map((sp) => (
          <div className="tbz-feld" key={sp.id}>
            <span className="tbz-feld-was">{t(sp.kopf)}</span>
            <div className="tbz-feld-wert">{sp.zelle(z)}</div>
          </div>
        ))}
      </div>
    ) : null)
    : undefined);
  const auf = (platz: Handyplatz) => spalten.filter((s) => plaetze[s.id] === platz);
  const kennungen = auf("kennung");
  const titel = auf("titel");
  const unter = auf("unter");
  const marken = auf("marke");

  const titelVon = (z: Z): ReactNode => (telefonblatt?.titel ? telefonblatt.titel(z) : (
    <>
      {kennungen.map((s) => {
        const w = s.zelle(z);
        return leer(w) ? null : <span key={s.id} className="tbz-kennung">{w}</span>;
      })}
      {titel.map((s) => {
        const w = s.zelle(z);
        const sache = alsSache(w);
        if (!sache) return <Fragment key={s.id}>{w}</Fragment>;
        return (
          <Fragment key={s.id}>
            {sache.name}
            {sache.nr ? <span className="tbz-nr">{sache.nr}</span> : null}
          </Fragment>
        );
      })}
    </>
  ));
  const unterVon = (z: Z): ReactNode => {
    if (telefonblatt?.unter) return telefonblatt.unter(z);
    const zusaetze = titel
      .map((s) => ({ id: `${s.id}-dazu`, w: alsSache(s.zelle(z))?.dazu }))
      .filter((x) => x.w !== undefined && x.w !== null && !leer(x.w));
    const teile = [
      ...unter.map((s) => ({ id: s.id, w: s.zelle(z) })).filter((x) => !leer(x.w)),
      ...zusaetze,
    ];
    if (!teile.length) return null;
    return teile.map((x, i) => (
      <Fragment key={x.id}>{i > 0 ? trenn : null}<span className="tbz-wert">{x.w}</span></Fragment>
    ));
  };
  const rechtsVon = (z: Z): ReactNode => {
    const teile = marken.map((s) => ({ id: s.id, w: s.zelle(z) })).filter((x) => !leer(x.w));
    if (!teile.length) return undefined;
    return <span className="tbz-marken">{teile.map((x) => <Fragment key={x.id}>{x.w}</Fragment>)}</span>;
  };

  const anzahl = bloecke.reduce((n, g) => n + g.zeilen.length, 0);
  /* Die offene Zeile: die eine, für die die Maske eine Unterzeile liefert.
     Ihre Bearbeitung steht im Blatt. */
  const offen = unterzeile
    ? bloecke.flatMap((g) => g.zeilen).find((z) => unterzeile(z) !== null) ?? null
    : null;

  if (laedt) {
    return (
      <div className="tbz" aria-busy="true">
        <Zeilenblatt>
          {Array.from({ length: ladezeilen }, (_, i) => (
            <div className="tbz-skelett" key={i} aria-hidden="true">
              <span style={{ width: `${[62, 48, 56, 40, 52, 44][i % 6]}%` }} />
              <span style={{ width: `${[38, 30, 44, 34, 28, 40][i % 6]}%` }} />
            </div>
          ))}
        </Zeilenblatt>
      </div>
    );
  }

  return (
    <div className="tbz" aria-label={t(schild)}>
      {anzahl === 0 && leerSatz ? <Leersatz>{leerSatz}</Leersatz> : null}
      {bloecke.map((g) => (g.zeilen.length ? (
        <section className="tbz-gruppe" key={g.id}>
          {g.kopf ? (
            <div className="hf-abschnitt-kopf">
              <h2>{g.kopf}</h2>
              <span className="hf-abschnitt-auskunft">{g.zahl ?? g.zeilen.length}</span>
            </div>
          ) : null}
          <Zeilenblatt>
            {g.zeilen.map((z) => (
              <Zweizeiler
                key={id(z)}
                beiKlick={() => klick(z)}
                punkt={punkt?.(z) ?? "ruhig"}
                titel={titelVon(z)}
                unter={unterVon(z)}
                rechts={rechtsVon(z)}
              />
            ))}
          </Zeilenblatt>
        </section>
      ) : null))}
      {fuss ? <div className="tbz-fuss">{fuss}</div> : null}
      {unterzeile ? (
        <Blattvonunten
          offen={offen !== null}
          schliessen={() => { if (offen !== null) klick(offen); }}
          titel={offen !== null ? (textVon(titelVon(offen)) || t(schild)) : t(schild)}
          rast="gross"
        >
          {offen !== null ? <div className="tbz-blatt">{unterzeile(offen)}</div> : null}
        </Blattvonunten>
      ) : null}
    </div>
  );
}
